import { getFontSizes } from './get-font-sizes.js';
import { getTruncationMethod } from './get-truncation-method.js';
import { measureNodeTextContent } from './measure-node-text-content.js';
import { renderTextToCanvas } from './render-text-to-canvas.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import type { TextSize } from '../../../../core/types/text-size.js';
import { getColorByContrast } from '../../../../core/utils/color-contrast.js';
import {
  getTextMeasurer,
  type TextMeasurer,
} from '../../../../core/utils/text-measure.js';
import {
  HONEYCOMB_DEFAULT_TEXT_ALIGNMENT,
  HONEYCOMB_LABEL_FONT_WEIGHT,
  HONEYCOMB_VALUE_FONT_WEIGHT,
  HONEYCOMB_VALUE_LABEL_FONT_SIZE,
} from '../../../constants.js';
import { CanvasTextAreaProps } from '../../../types/canvas-text-area.js';
import type { HoneycombLabelsDisplay } from '../../../types/honeycomb.js';

const topAndBottomPadding = 2;

type RenderParams = {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  renderedName: string;
  renderedValue: string;
  nameHeight: number;
  valueHeight: number;
  ableToRenderName: boolean;
  ableToRenderValue: boolean;
  ableToRenderBoth: boolean;
  textArea: CanvasTextAreaProps;
  value: string | number;
  truncationMode: TruncationMode;
  labelFontSize: number;
  valueFontSize: number;
  labelMeasure: TextMeasurer;
};

const renderNameMode = ({
  ctx,
  x,
  y,
  renderedName,
  nameHeight,
  ableToRenderName,
  labelFontSize,
}: RenderParams) => {
  if (ableToRenderName) {
    renderTextToCanvas(
      ctx,
      renderedName,
      'label',
      x,
      y + nameHeight / 2,
      labelFontSize,
    );
  }
};

const renderValueMode = (params: RenderParams) => {
  const {
    ctx,
    x,
    y,
    renderedValue,
    valueHeight,
    nameHeight,
    ableToRenderValue,
    ableToRenderName,
    textArea,
    value,
    truncationMode,
    labelFontSize,
    valueFontSize,
    labelMeasure,
  } = params;
  if (ableToRenderValue) {
    renderTextToCanvas(
      ctx,
      renderedValue,
      'value',
      x,
      y + valueHeight / 2,
      valueFontSize,
    );
  } else if (ableToRenderName) {
    // Value font doesn't fit but label font does — render value at the smaller size.
    const cropValueAsLabel = getTruncationMethod(
      textArea,
      value.toString(),
      truncationMode,
      labelMeasure,
      'label',
      labelFontSize,
    );
    renderTextToCanvas(
      ctx,
      cropValueAsLabel(),
      'label',
      x,
      y + nameHeight / 2,
      labelFontSize,
    );
  }
};

const renderAllMode = ({
  ctx,
  x,
  y,
  renderedName,
  renderedValue,
  nameHeight,
  valueHeight,
  ableToRenderBoth,
  ableToRenderName,
  ableToRenderValue,
  labelFontSize,
  valueFontSize,
}: RenderParams) => {
  if (ableToRenderBoth) {
    renderTextToCanvas(ctx, renderedName, 'label', x, y - 2, labelFontSize);
    renderTextToCanvas(
      ctx,
      renderedValue,
      'value',
      x,
      y + nameHeight + nameHeight * 0.5,
      valueFontSize,
    );
  } else if (ableToRenderName) {
    renderTextToCanvas(
      ctx,
      renderedName,
      'label',
      x,
      y + nameHeight / 2,
      labelFontSize,
    );
  } else if (ableToRenderValue) {
    renderTextToCanvas(
      ctx,
      renderedValue,
      'value',
      x,
      y + valueHeight / 2,
      valueFontSize,
    );
  }
};

export const drawLabelAndValue = (
  ctx: CanvasRenderingContext2D,
  value: string | number,
  textArea: CanvasTextAreaProps,
  backgroundColor: string,
  name: string = '',
  truncationMode: TruncationMode = 'middle',
  labelsDisplay: HoneycombLabelsDisplay,
  textSize: TextSize = HONEYCOMB_VALUE_LABEL_FONT_SIZE,
) => {
  const availableWidth = textArea.width - topAndBottomPadding * 2;
  const availableHeight = textArea.height;
  const { valueFontSize, labelFontSize } = getFontSizes(
    ctx,
    textSize,
    value.toString(),
    availableWidth,
    availableHeight,
  );
  const valueMeasure = getTextMeasurer(ctx, valueFontSize, {
    fontWeight: HONEYCOMB_VALUE_FONT_WEIGHT,
  });
  const labelMeasure = getTextMeasurer(ctx, labelFontSize, {
    fontWeight: HONEYCOMB_LABEL_FONT_WEIGHT,
  });

  const { x, y } = textArea;
  const croppingTextArea = { ...textArea, width: availableWidth };
  const cropNameToFit = getTruncationMethod(
    croppingTextArea,
    name,
    truncationMode,
    labelMeasure,
    'label',
    labelFontSize,
  );
  const cropValueToFit =
    typeof value === 'string'
      ? getTruncationMethod(
          croppingTextArea,
          value,
          truncationMode,
          valueMeasure,
          'value',
          valueFontSize,
        )
      : () => value.toString();

  const {
    valueHeight,
    nameHeight,
    ableToRenderNothing,
    ableToRenderBoth,
    valueWidth,
    nameWidth,
    ableToRenderName,
    ableToRenderValue,
  } = measureNodeTextContent(
    value,
    name,
    textArea,
    labelMeasure,
    valueMeasure,
    labelFontSize,
    valueFontSize,
  );

  if (ableToRenderNothing) {
    return;
  }

  ctx.textAlign = HONEYCOMB_DEFAULT_TEXT_ALIGNMENT;
  ctx.fillStyle = getColorByContrast(backgroundColor);

  const renderParams: RenderParams = {
    ctx,
    x,
    y,
    renderedName: availableWidth > nameWidth ? name : cropNameToFit(),
    renderedValue:
      availableWidth >= valueWidth ? value.toString() : cropValueToFit(),
    nameHeight,
    valueHeight,
    ableToRenderName,
    ableToRenderValue,
    ableToRenderBoth,
    textArea,
    value,
    truncationMode,
    labelFontSize,
    valueFontSize,
    labelMeasure,
  };

  if (labelsDisplay === 'name') {
    renderNameMode(renderParams);
  } else if (labelsDisplay === 'value') {
    renderValueMode(renderParams);
  } else if (labelsDisplay === 'all') {
    renderAllMode(renderParams);
  }
};
